<template>
  <v-container>
    <v-layout row wrap>
      <v-flex xs12 md8 offset-md2>
        <h1 class="primary--text mb-4" v-if="$route.query.type">Create New {{ $route.query.type }}</h1>
        <h1 class="primary--text mb-4" v-else>Create New User</h1>
        <app-alert class="mb-3" type="error" :message="error" @dismissed="error = false"/>

        <v-card>
          <v-card-title class="subheading font-weight-bold px-4">
            <span>New User Information</span>
            <v-spacer></v-spacer>
            <v-icon @click="$router.go(-1)">close</v-icon>
          </v-card-title>
          <v-divider></v-divider>

          <v-container class="px-4" grid-list-lg>
            <v-layout justify-space-between row wrap>
              <v-flex xs12>
                <v-select
                  label="Pick a User Role"
                  v-model="role"
                  :items="roleOptions"
                  @change="onRoleChange"
                  required
                ></v-select>
              </v-flex>
              <v-flex xs12>
                <!-- parentsLoading Progress -->
                <v-progress-circular :indeterminate="true" :size="18" :width="2" v-if="parentsLoading"></v-progress-circular>
              </v-flex>
            </v-layout>

            <!-- New User Form -->
            <v-form ref="form" @submit.prevent="doCreateUser" v-if="role">
              <!-- Check for admin or parents list length -->
              <v-layout justify-space-between row wrap v-if="role === roles.admin || parents.length">
                <v-flex xs12 v-if="role !== roles.admin">
                  <v-select
                    :label="agent.parent === 'state' ? 'State' : 'Partner'"
                    type="text"
                    :items="parents"
                    v-model="agent.parent_id"
                    :rules="[rules.isRequired]"
                    required
                  ></v-select>
                </v-flex>

                <v-flex xs12>
                  <v-text-field
                    label="Full Name"
                    type="text"
                    v-model="user.name"
                    :rules="[rules.isRequired]"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Email Address"
                    type="email"
                    v-model="user.email"
                    :rules="[rules.isEmail]"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 v-if="role !== roles.admin">
                  <v-text-field
                    label="Phone Number"
                    type="number"
                    v-model="agent.phone"
                    :rules="[rules.isPhone]"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Password"
                    type="password"
                    v-model="user.hash"
                    :rules="[rules.isRequired]"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Confirm Password"
                    type="password"
                    v-model="user.c_hash"
                    :rules="[rules.isRequired, matchedPasswords]"
                    required
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout class="mt-4" v-if="role === roles.admin || parents.length">
                <v-btn depressed color="accent" type="submit" :disabled="isSaving">
                  Create
                  <v-progress-circular
                    class="ml-3"
                    :indeterminate="true"
                    :size="18"
                    :width="2"
                    v-if="isSaving"
                  ></v-progress-circular>
                </v-btn>
              </v-layout>
              
              <app-alert v-if="role !== roles.admin && !parents.length && !parentsLoading"
                type="error"
                :outline="true"
                :message="`We could not find any ${this.agent.parent} in the database. You must create one, before adding a new user.`"
              />

            </v-form>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import rolesData from '@/data/roles'
import { roleOptions } from '@/data/options'

import { USER_CREATE } from '@/store/actions/user'
import { STATE_FETCH } from '@/store/actions/state'
import { AGENCY_FETCH } from '@/store/actions/agency'

export default {
  name: 'CreateUser',
  created () {
    this.onRoleChange()
  },
  data () {
    return {
      role: this.$route.query.role || '',
      roles: rolesData,
      user: {
        name: '',
        email: '',
        status: 'active',
        hash: '',
        c_hash: ''
      },
      agent: {
        phone: '',
        parent: '',
        parent_id: ''
      },
      error: false,
      parentsLoading: false,
      isSaving: false,
      rules: {
        isRequired: value => this.$validator.isRequired(value),
        isEmail: value => this.$validator.isEmail(value),
        isPhone: value => this.$validator.isPhone(value)
      }
    }
  },
  computed: {
    matchedPasswords () {
      return this.user.hash !== this.user.c_hash ? 'Passwords do not match' : true
    },
    roleOptions () {
      return roleOptions.filter(item => {
        return this.$can(item.can)
      })
    },
    parents () {
      if (rolesData.stateAgents.includes(this.role)) {
        let stateOptions = this.$store.getters.statesAsOptions
        // If state agent, show only agents state in list
        if (this.currentUser.parent && this.currentUser.parent === 'state') {
          const state = stateOptions.find(stateOption => this.currentUser.parent_id === stateOption.value)
          stateOptions = [state]
        }
        return stateOptions
      }
      else if (rolesData.agencyAgents.includes(this.role)) {
        let agencyOptions = this.$store.getters.agenciesAsOptions
        // If state agent, show only agents state in list
        if (this.currentUser.parent && this.currentUser.parent === 'agency') {
          const agency = agencyOptions.find(agencyOption => this.currentUser.parent_id === agencyOption.value)
          agencyOptions = [agency]
        }
        return agencyOptions
      }
      return []
    },
    ...mapGetters({
      currentUser: 'currentUser'
    })
  },
  methods: {
    onRoleChange () {
      this.error = false
      this.parentsLoading = true
      this.agent.parent = '' // set parent to empty for security reasons ;)
      if (rolesData.stateAgents.includes(this.role)) {
        this.agent.parent = 'state'
        this.$store.dispatch(STATE_FETCH).finally(_ => (this.parentsLoading = false))
      }
      else if (rolesData.agencyAgents.includes(this.role)) {
        this.agent.parent = 'agency'
        this.$store.dispatch(AGENCY_FETCH).finally(_ => (this.parentsLoading = false))
      }
      else {
        this.parentsLoading = false
      }
    },
    doCreateUser () {
      if (!this.$refs.form.validate()) return
      this.error = false
      this.isSaving = true
      let isAdmin = true
      const newUser = Object.assign({}, this.user)
      delete newUser.c_hash // delete confirm password prop

      if (this.agent.parent !== '') {
        isAdmin = false
        newUser.role = this.role
        newUser.phone = this.agent.phone
        newUser.parent = this.agent.parent
        newUser.parent_id = this.agent.parent_id
      }

      this.$store.dispatch(USER_CREATE, { isAdmin, user: newUser })
        .then(() => {
          const next = this.$route.query.next || '/users'
          this.$router.push(`${next}?userCreated=1`)
        })
        .catch(error => (this.error = error.message))
        .finally(() => (this.isSaving = false))
    }
  }
}
</script>
